import React, { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import { inviteClubMember } from "../funtionsBD/Mainservice";
import {
  PLAYER_INVITE_MAX_BATCH,
  isValidInviteEmail,
  parsePlayerInviteList,
} from "../../utils/parsePlayerInviteList";
import { scrollFieldAboveKeyboard } from "../../hooks/useWizardViewportHeight";
import "./DigipingPlayerInviteRoster.css";

function emptyDraft() {
  return { firstName: "", lastName: "", email: "" };
}

function rowFromDraft(draft) {
  return {
    first_name: String(draft.firstName || "").trim(),
    last_name: String(draft.lastName || "").trim(),
    email: String(draft.email || "").trim().toLowerCase(),
  };
}

export default function DigipingPlayerInviteRoster({
  expanded,
  onToggle,
  onSent,
  clubId,
  lng,
} = {}) {
  const { t } = useTranslation();
  const lngOpts = lng ? { lng } : undefined;
  const [added, setAdded] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const draftPlayer = useMemo(() => rowFromDraft(draft), [draft]);
  const players = useMemo(() => {
    const fromAdded = added.map((row) => ({
      first_name: row.firstName,
      last_name: row.lastName,
      email: row.email,
    }));
    return draftPlayer.email ? [...fromAdded, draftPlayer] : fromAdded;
  }, [added, draftPlayer]);
  const invalid = players.find((p) => !isValidInviteEmail(p.email));
  const canSend = players.length > 0 && !invalid && !sending;
  const canAddMore = added.length + (draftPlayer.email ? 1 : 0) < PLAYER_INVITE_MAX_BATCH;
  const draftFilled = Boolean(
    draft.firstName.trim() || draft.lastName.trim() || draft.email.trim()
  );

  const patchDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setError("");
    setResult(null);
  };

  const commitDraft = () => {
    if (!draftPlayer.email) {
      setError(t("digipingInviteEmpty", lngOpts));
      return false;
    }
    if (!isValidInviteEmail(draftPlayer.email)) {
      setError(t("digipingInviteInvalidEmail", lngOpts));
      return false;
    }
    if (added.length >= PLAYER_INVITE_MAX_BATCH) {
      setError(t("digipingInviteMax", { count: PLAYER_INVITE_MAX_BATCH, ...lngOpts }));
      return false;
    }
    setAdded((prev) => [
      ...prev,
      {
        id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        firstName: draftPlayer.first_name,
        lastName: draftPlayer.last_name,
        email: draftPlayer.email,
      },
    ]);
    setDraft(emptyDraft());
    setError("");
    return true;
  };

  const removeAdded = (id) => {
    setAdded((prev) => prev.filter((row) => row.id !== id));
  };

  const applyPaste = () => {
    const parsed = parsePlayerInviteList(pasteText, { max: PLAYER_INVITE_MAX_BATCH });
    if (!parsed.length) {
      setError(t("digipingInvitePasteEmpty", lngOpts));
      return;
    }
    setAdded(
      parsed.map((p, i) => ({
        id: `row_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 8)}`,
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        email: String(p.email || "").trim().toLowerCase(),
      }))
    );
    setDraft(emptyDraft());
    setPasteOpen(false);
    setPasteText("");
    setError("");
    setResult(null);
  };

  const handleSend = async () => {
    if (draftPlayer.email && !isValidInviteEmail(draftPlayer.email)) {
      setError(t("digipingInviteInvalidEmail", lngOpts));
      return;
    }
    if (!canSend) {
      setError(
        invalid
          ? t("digipingInviteInvalidEmail", lngOpts)
          : t("digipingInviteEmpty", lngOpts)
      );
      return;
    }
    if (players.length > PLAYER_INVITE_MAX_BATCH) {
      setError(t("digipingInviteMax", { count: PLAYER_INVITE_MAX_BATCH, ...lngOpts }));
      return;
    }
    setSending(true);
    setError("");
    try {
      const response = await inviteClubMember({
        club_id: clubId || undefined,
        players,
      });
      if (response.status === "error") {
        setError(response.message || t("error_sending_invitation", lngOpts));
        return;
      }
      const data = response.data || {};
      setResult(data);
      const sent = Array.isArray(data.sent) ? data.sent : [];
      const failed = Array.isArray(data.failed) ? data.failed : [];
      setAdded((prev) =>
        prev.filter(
          (row) =>
            !sent.some((s) => String(s.email || "").toLowerCase() === row.email) &&
            !failed.some((f) => String(f.email || "").toLowerCase() === row.email)
        )
      );
      if (
        sent.some((s) => String(s.email || "").toLowerCase() === draftPlayer.email) ||
        failed.some((f) => String(f.email || "").toLowerCase() === draftPlayer.email)
      ) {
        setDraft(emptyDraft());
      }
      if (!sent.length && failed.length) {
        const reasons = failed
          .map((f) => f.reason || f.email)
          .filter(Boolean)
          .slice(0, 3)
          .join("; ");
        setError(reasons || t("error_sending_invitation", lngOpts));
      }
      onSent?.(data, players);
    } catch (err) {
      setError(err.message || t("error_sending_invitation", lngOpts));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`dp-invite${expanded ? " dp-invite--open" : ""}`}>
      <button
        type="button"
        className="dp-invite__toggle"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <PersonAddAltOutlinedIcon fontSize="small" />
        <span className="dp-invite__toggle-copy">
          <strong>{t("digipingInvitePlayersTitle", lngOpts)}</strong>
          <span>{t("digipingInvitePlayersHint", lngOpts)}</span>
        </span>
        <ExpandMoreIcon className="dp-invite__chevron" fontSize="small" />
      </button>

      {expanded ? (
        <div className="dp-invite__panel">
          <p className="dp-invite__note">{t("digipingInviteProfilingNote", lngOpts)}</p>

          {added.length ? (
            <ul className="dp-invite__chips">
              {added.map((row) => (
                <li key={row.id} className="dp-invite__chip">
                  <span>
                    {[row.firstName, row.lastName].filter(Boolean).join(" ") || row.email}
                    {row.firstName || row.lastName ? ` · ${row.email}` : ""}
                  </span>
                  <button
                    type="button"
                    className="dp-invite__icon-btn"
                    onClick={() => removeAdded(row.id)}
                    aria-label={t("digipingInviteRemoveRow", lngOpts)}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="dp-invite__draft">
            <input
              type="text"
              autoComplete="given-name"
              enterKeyHint="next"
              aria-label={t("firstname", lngOpts)}
              placeholder={t("firstname", lngOpts)}
              value={draft.firstName}
              onFocus={(e) => scrollFieldAboveKeyboard(e.target)}
              onChange={(e) => patchDraft({ firstName: e.target.value })}
            />
            <input
              type="text"
              autoComplete="family-name"
              enterKeyHint="next"
              aria-label={t("lastname", lngOpts)}
              placeholder={t("lastname", lngOpts)}
              value={draft.lastName}
              onFocus={(e) => scrollFieldAboveKeyboard(e.target)}
              onChange={(e) => patchDraft({ lastName: e.target.value })}
            />
            <input
              type="email"
              autoComplete="email"
              enterKeyHint="done"
              inputMode="email"
              aria-label={t("mail", lngOpts)}
              placeholder={t("mail", lngOpts)}
              value={draft.email}
              onFocus={(e) => scrollFieldAboveKeyboard(e.target)}
              onChange={(e) => patchDraft({ email: e.target.value })}
            />
          </div>

          <div className="dp-invite__row-actions">
            <button
              type="button"
              className="dp-invite__link"
              onClick={commitDraft}
              disabled={!canAddMore || !draftFilled}
            >
              <AddIcon fontSize="small" />
              {t("digipingInviteAddRow", lngOpts)}
            </button>
            <button
              type="button"
              className="dp-invite__link"
              onClick={() => setPasteOpen((open) => !open)}
            >
              {t("digipingInvitePaste", lngOpts)}
            </button>
          </div>

          {pasteOpen ? (
            <div className="dp-invite__paste">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                onFocus={(e) => scrollFieldAboveKeyboard(e.target)}
                rows={4}
                placeholder={t("digipingInvitePastePlaceholder", lngOpts)}
                aria-label={t("digipingInvitePaste", lngOpts)}
              />
              <button type="button" className="dp-invite__secondary" onClick={applyPaste}>
                {t("digipingInvitePasteApply", lngOpts)}
              </button>
            </div>
          ) : null}

          {error ? <p className="dp-invite__error">{error}</p> : null}
          {result ? (
            <p className="dp-invite__success">
              {result.sent?.length
                ? t("digipingInviteSentSummary", {
                    count: result.sent.length,
                    ...lngOpts,
                  })
                : null}
              {result.sent?.some((s) => s.dual_role)
                ? ` ${t("digipingInviteDualRoleNote", lngOpts) || "Player role added for existing admin/coach accounts."}`
                : ""}
              {result.failed?.length
                ? ` ${t("digipingInviteFailedSummary", {
                    count: result.failed.length,
                    ...lngOpts,
                  })}`
                : ""}
            </p>
          ) : null}

          <div className="dp-invite__footer">
            <button
              type="button"
              className="dp-invite__send"
              onClick={handleSend}
              disabled={!canSend}
            >
              <SendIcon fontSize="small" />
              {sending
                ? t("digipingInviteSending", lngOpts)
                : t("digipingInviteSend", { count: players.length, ...lngOpts })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
